import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { t } from "@/lib/i18n/interpolate";
import { AMOUNT_UNITS, molesFrom } from "@/lib/tool-kit/chem-reactions/amounts";
import type { Species } from "@/lib/tool-kit/chem-reactions/balance";
import { speciesMass } from "@/lib/tool-kit/chem-reactions/stoichiometry";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/**
 * One amount box (value and unit) per reactant, shared by the limiting-reactant and
 * percent-yield calculators. Widgets spread `REACTANT_AMOUNT_STRINGS` into their strings.
 */
export const REACTANT_AMOUNT_STRINGS = {
  amountsHeading: "How much of each reactant do you have?",
  amountLabel: "Amount of {species}",
  amountHint:
    "Leave it blank if {species} is in unlimited supply, such as oxygen from the air.",
  unitLabel: "Unit for {species}",
  badAmount: "The amount of {species} must be a number greater than zero.",
  badMass:
    "A mass or volume of electrons is not meaningful. Give {species} in moles, or leave it blank.",
  needsGas:
    "A gas volume needs a valid temperature and pressure. Enter a temperature above absolute zero and a positive pressure.",
} as const;

type AmountStrings = Record<keyof typeof REACTANT_AMOUNT_STRINGS, string>;

export interface AmountState {
  value: string;
  unit: string;
}

export const EMPTY_AMOUNT: AmountState = { value: "", unit: "g" };

const UNIT_OPTIONS = AMOUNT_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));

export type ReadAmounts =
  | { ok: true; moles: (number | null)[]; raws: string[] }
  | { ok: false; message: string };

/** The moles of every reactant (null where left blank), or the message saying what is wrong. */
export function readAmounts(
  species: readonly Species[],
  states: readonly AmountState[],
  molarVolumeValue: number | null,
  strings: AmountStrings
): ReadAmounts {
  const moles: (number | null)[] = [];
  const raws: string[] = [];
  for (const [index, item] of species.entries()) {
    const state = states[index] ?? EMPTY_AMOUNT;
    if (item.side === "product" || state.value.trim() === "") {
      moles.push(null);
      continue;
    }
    const value = parseMeasuredNumber(state.value);
    if (value === null || !(value > 0)) {
      return {
        ok: false,
        message: t(strings.badAmount, { species: item.label }),
      };
    }
    const read = molesFrom(
      value,
      state.unit,
      speciesMass(item).total,
      molarVolumeValue
    );
    if (!read.ok) {
      return {
        ok: false,
        message:
          read.reason === "noGas"
            ? strings.needsGas
            : t(strings.badMass, { species: item.label }),
      };
    }
    moles.push(read.moles);
    raws.push(state.value);
  }
  return { ok: true, moles, raws };
}

/** A copy of `states` with `patch` applied at `index`, padding gaps with empty amounts. */
export function patchAmount(
  states: readonly AmountState[],
  index: number,
  patch: Partial<AmountState>
): AmountState[] {
  const next = [...states];
  while (next.length <= index) {
    next.push({ ...EMPTY_AMOUNT });
  }
  next[index] = { ...next[index], ...patch };
  return next;
}

export function ReactantAmountFields({
  idPrefix,
  species,
  states,
  onChange,
  strings,
}: {
  idPrefix: string;
  species: readonly Species[];
  states: readonly AmountState[];
  onChange: (index: number, patch: Partial<AmountState>) => void;
  strings: AmountStrings;
}) {
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.amountsHeading}</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        {species.map((item, index) =>
          item.side === "reactant" ? (
            <QuantityInput
              hint={t(strings.amountHint, { species: item.label })}
              id={`${idPrefix}-amount-${index}`}
              key={`${index}-${item.label}`}
              label={t(strings.amountLabel, { species: item.label })}
              onUnitChange={(unit) => onChange(index, { unit })}
              onValueChange={(value) => onChange(index, { value })}
              unit={states[index]?.unit ?? EMPTY_AMOUNT.unit}
              unitLabel={t(strings.unitLabel, { species: item.label })}
              units={UNIT_OPTIONS}
              value={states[index]?.value ?? ""}
            />
          ) : null
        )}
      </div>
    </>
  );
}
